/**
 * 标准配合求解计划。
 *
 * 文件职责：
 * 1. 基于 Mate 组件连通图生成稳定求解顺序；
 * 2. 为全浮动配合图选择可预测锚点；
 * 3. 避免主求解器按输入数组顺序直接决定浮动组件根节点。
 */

import type { AssemblyMateDefinition } from '../definition';
import type { AssemblyMateSolverComponentState } from './StandardMateSolverTypes';

/** 无法从当前图锚点到达的距离。 */
const UNREACHABLE_GRAPH_DISTANCE = Number.POSITIVE_INFINITY;

/**
 * 标准配合求解计划结果。
 */
export interface StandardMateSolvePlan {
  /** 按图锚点排序后的 Mate Definition。 */
  readonly mateDefinitions: readonly AssemblyMateDefinition[];
  /** 本轮求解初始应视为上游锚点的组件实例 ID。 */
  readonly initialStationaryComponentIds: readonly string[];
  /**
   * 组件到当前求解锚点的图距离。
   *
   * 设计说明：
   * 该距离只描述约束传播方向。主求解器可据此优先移动离交互根更远的一侧，
   * 避免单条 Mate 按类型反向拉回当前被拖动组件。
   */
  readonly componentGraphDistances: ReadonlyMap<string, number>;
}

/**
 * 读取 Mate 两端组件 ID。
 *
 * @param mate Mate Definition。
 * @returns 两端组件 ID。
 */
function resolveMateComponentIds(mate: AssemblyMateDefinition): readonly [string, string] {
  return [
    mate.firstEntity.componentInstanceId,
    mate.secondEntity.componentInstanceId,
  ] as const;
}

/**
 * 判断组件是否参与当前求解输入。
 *
 * @param components 组件状态映射。
 * @param componentInstanceId 组件实例 ID。
 * @returns 是否存在组件状态。
 */
function hasSolverComponent(
  components: Readonly<Record<string, AssemblyMateSolverComponentState>>,
  componentInstanceId: string,
): boolean {
  return Boolean(components[componentInstanceId]);
}

/**
 * 判断组件是否固定。
 *
 * @param component 组件状态。
 * @returns 是否固定。
 */
function isFixedComponent(component: AssemblyMateSolverComponentState | undefined): boolean {
  return component?.fixationState === 'fixed';
}

/**
 * 排序字符串。
 *
 * @param left 左值。
 * @param right 右值。
 * @returns 排序结果。
 */
function compareText(left: string, right: string): number {
  return left.localeCompare(right);
}

/**
 * 从 Mate 图中收集有效组件 ID。
 *
 * @param mateDefinitions Mate Definition 列表。
 * @param components 组件状态映射。
 * @returns 去重后的组件 ID。
 */
function collectMateGraphComponentIds(
  mateDefinitions: readonly AssemblyMateDefinition[],
  components: Readonly<Record<string, AssemblyMateSolverComponentState>>,
): readonly string[] {
  const componentIds = new Set<string>();
  mateDefinitions.forEach((mate) => {
    const [firstComponentId, secondComponentId] = resolveMateComponentIds(mate);
    if (hasSolverComponent(components, firstComponentId)) {
      componentIds.add(firstComponentId);
    }
    if (hasSolverComponent(components, secondComponentId)) {
      componentIds.add(secondComponentId);
    }
  });
  return [...componentIds].sort(compareText);
}

/**
 * 向邻接表写入双向边。
 *
 * @param adjacency 邻接表。
 * @param firstComponentId 第一组件 ID。
 * @param secondComponentId 第二组件 ID。
 */
function appendAdjacencyEdge(
  adjacency: Map<string, Set<string>>,
  firstComponentId: string,
  secondComponentId: string,
): void {
  const firstNeighbors = adjacency.get(firstComponentId) ?? new Set<string>();
  firstNeighbors.add(secondComponentId);
  adjacency.set(firstComponentId, firstNeighbors);

  const secondNeighbors = adjacency.get(secondComponentId) ?? new Set<string>();
  secondNeighbors.add(firstComponentId);
  adjacency.set(secondComponentId, secondNeighbors);
}

/**
 * 构建 Mate 组件图邻接表。
 *
 * @param mateDefinitions Mate Definition 列表。
 * @param components 组件状态映射。
 * @returns 邻接表。
 */
function buildMateGraphAdjacency(
  mateDefinitions: readonly AssemblyMateDefinition[],
  components: Readonly<Record<string, AssemblyMateSolverComponentState>>,
): Map<string, Set<string>> {
  const adjacency = new Map<string, Set<string>>();
  mateDefinitions.forEach((mate) => {
    if (mate.suppressed) {
      return;
    }
    const [firstComponentId, secondComponentId] = resolveMateComponentIds(mate);
    if (
      !hasSolverComponent(components, firstComponentId)
      || !hasSolverComponent(components, secondComponentId)
      || firstComponentId === secondComponentId
    ) {
      return;
    }
    appendAdjacencyEdge(adjacency, firstComponentId, secondComponentId);
  });
  return adjacency;
}

/**
 * 解析当前图中的固定组件锚点。
 *
 * @param componentIds 图组件 ID。
 * @param components 组件状态映射。
 * @returns 固定组件 ID。
 */
function resolveFixedAnchorComponentIds(
  componentIds: readonly string[],
  components: Readonly<Record<string, AssemblyMateSolverComponentState>>,
): readonly string[] {
  return componentIds.filter((componentId) => isFixedComponent(components[componentId]));
}

/**
 * 解析当前图中的交互驱动锚点。
 *
 * @param componentIds 图组件 ID。
 * @param drivenComponentIds 交互驱动组件 ID。
 * @returns 驱动组件 ID。
 */
function resolveDrivenAnchorComponentIds(
  componentIds: readonly string[],
  drivenComponentIds: ReadonlySet<string>,
): readonly string[] {
  return componentIds.filter((componentId) => drivenComponentIds.has(componentId));
}

/**
 * 解析 PropertyManager 预览阶段的交互基准锚点。
 *
 * @param componentIds 图组件 ID。
 * @param previewAnchorComponentIds 预览锚点组件 ID。
 * @returns 可参与当前求解图的预览锚点组件 ID。
 */
function resolvePreviewAnchorComponentIds(
  componentIds: readonly string[],
  previewAnchorComponentIds: ReadonlySet<string>,
): readonly string[] {
  return componentIds.filter((componentId) => previewAnchorComponentIds.has(componentId));
}

/**
 * 为没有固定/驱动组件的浮动图解析默认锚点。
 *
 * 设计说明：
 * 1. 单条 Mate 仍保留 SOLIDWORKS 默认策略：移动第二选择去匹配第一选择；
 * 2. 多条 Mate 的全浮动连通图不能由输入数组顺序决定根节点，因此使用组件 ID 字典序作为稳定锚点；
 * 3. 该策略不声称等价完整自由度求解，只用于消除当前前端纯刚体求解器的输入顺序依赖。
 *
 * @param mateDefinitions Mate Definition 列表。
 * @param componentIds 当前图组件 ID。
 * @param components 组件状态映射。
 * @returns 默认锚点组件 ID。
 */
function resolveFallbackAnchorComponentIds(
  mateDefinitions: readonly AssemblyMateDefinition[],
  componentIds: readonly string[],
  components: Readonly<Record<string, AssemblyMateSolverComponentState>>,
): readonly string[] {
  const onlyMate = mateDefinitions.length === 1 ? mateDefinitions[0] : null;
  if (onlyMate && hasSolverComponent(components, onlyMate.firstEntity.componentInstanceId)) {
    return [onlyMate.firstEntity.componentInstanceId];
  }
  return componentIds.length > 0 ? [componentIds[0]!] : [];
}

/**
 * 解析求解锚点。
 *
 * @param mateDefinitions Mate Definition 列表。
 * @param components 组件状态映射。
 * @param drivenComponentIds 交互驱动组件 ID。
 * @param previewAnchorComponentIds PropertyManager 预览阶段的交互基准组件 ID。
 * @returns 锚点组件 ID。
 */
function resolveAnchorComponentIds(
  mateDefinitions: readonly AssemblyMateDefinition[],
  components: Readonly<Record<string, AssemblyMateSolverComponentState>>,
  drivenComponentIds: ReadonlySet<string>,
  previewAnchorComponentIds: ReadonlySet<string>,
): readonly string[] {
  const componentIds = collectMateGraphComponentIds(mateDefinitions, components);
  const fixedAnchors = resolveFixedAnchorComponentIds(componentIds, components);
  if (fixedAnchors.length > 0) {
    return fixedAnchors;
  }

  const drivenAnchors = resolveDrivenAnchorComponentIds(componentIds, drivenComponentIds);
  if (drivenAnchors.length > 0) {
    return drivenAnchors;
  }

  const previewAnchors = resolvePreviewAnchorComponentIds(componentIds, previewAnchorComponentIds);
  if (previewAnchors.length > 0) {
    return previewAnchors;
  }

  return resolveFallbackAnchorComponentIds(mateDefinitions, componentIds, components);
}

/**
 * 计算组件到锚点的图距离。
 *
 * @param adjacency Mate 组件图邻接表。
 * @param anchors 锚点组件 ID。
 * @returns 图距离映射。
 */
function resolveComponentGraphDistances(
  adjacency: Map<string, Set<string>>,
  anchors: readonly string[],
): ReadonlyMap<string, number> {
  const distances = new Map<string, number>();
  const queue: string[] = [];

  anchors.forEach((componentId) => {
    distances.set(componentId, 0);
    queue.push(componentId);
  });

  for (let cursor = 0; cursor < queue.length; cursor += 1) {
    const componentId = queue[cursor]!;
    const distance = distances.get(componentId) ?? 0;
    const neighbors = [...(adjacency.get(componentId) ?? [])].sort(compareText);
    neighbors.forEach((neighborId) => {
      if (distances.has(neighborId)) {
        return;
      }
      distances.set(neighborId, distance + 1);
      queue.push(neighborId);
    });
  }

  return distances;
}

/**
 * 读取 Mate 距离排序值。
 *
 * @param mate Mate Definition。
 * @param distances 组件图距离。
 * @returns 距离排序值。
 */
function resolveMateGraphDistanceRank(
  mate: AssemblyMateDefinition,
  distances: ReadonlyMap<string, number>,
): number {
  const [firstComponentId, secondComponentId] = resolveMateComponentIds(mate);
  const firstDistance = distances.get(firstComponentId) ?? UNREACHABLE_GRAPH_DISTANCE;
  const secondDistance = distances.get(secondComponentId) ?? UNREACHABLE_GRAPH_DISTANCE;
  return Math.min(firstDistance, secondDistance);
}

/**
 * 创建标准配合求解计划。
 *
 * @param input 求解计划输入。
 * @returns 求解计划。
 */
export function createStandardMateSolvePlan(input: {
  /** 参与求解的 Mate Definition。 */
  readonly mateDefinitions: readonly AssemblyMateDefinition[];
  /** 组件状态映射。 */
  readonly components: Readonly<Record<string, AssemblyMateSolverComponentState>>;
  /** 交互驱动组件 ID。 */
  readonly drivenComponentIds: ReadonlySet<string>;
  /** PropertyManager 预览阶段的交互基准组件 ID。 */
  readonly previewAnchorComponentIds?: ReadonlySet<string>;
}): StandardMateSolvePlan {
  const anchorComponentIds = resolveAnchorComponentIds(
    input.mateDefinitions,
    input.components,
    input.drivenComponentIds,
    input.previewAnchorComponentIds ?? new Set<string>(),
  );
  const adjacency = buildMateGraphAdjacency(input.mateDefinitions, input.components);
  const distances = resolveComponentGraphDistances(adjacency, anchorComponentIds);
  const indexedMates = input.mateDefinitions.map((mate, index) => ({ mate, index }));

  return {
    initialStationaryComponentIds: anchorComponentIds,
    componentGraphDistances: distances,
    mateDefinitions: indexedMates
      .sort((left, right) => {
        if (left.mate.suppressed !== right.mate.suppressed) {
          return left.mate.suppressed ? 1 : -1;
        }
        const leftRank = resolveMateGraphDistanceRank(left.mate, distances);
        const rightRank = resolveMateGraphDistanceRank(right.mate, distances);
        if (leftRank !== rightRank) {
          return leftRank - rightRank;
        }
        const mateIdOrder = compareText(left.mate.mateId, right.mate.mateId);
        return mateIdOrder !== 0 ? mateIdOrder : left.index - right.index;
      })
      .map((entry) => entry.mate),
  };
}
